'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Modal from '@/ui/modal';
import Button from '@/ui/button';
import Input from '@/ui/input';
import Checkbox from '@/ui/checkbox';
import Link from 'next/link';
import axios from 'axios';

import useAuthStore from '@/store/auth';
import useModalStore from '@/store/modalStore';
import styles from './styles.module.css';

const SignUp = ({ dict }) => {
  const { create_account_modal } = dict.modal;
  const { log_in, sign_up } = dict.button;

  const { login, logout, hydrate } = useAuthStore();

  const {
    openSignUpModal,
    closeSignUpModal,
    isSignUpModalOpen,
    openLoginModal,
  } = useModalStore();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isLoginDisabled = useMemo(() => {
    return (
      name.trim() === '' ||
      email.trim() === '' ||
      password.trim() === '' ||
      !isCheckboxChecked
    );
  }, [name, email, password, isCheckboxChecked]);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const apiUrl = useMemo(() => {
    // важливо: Next.js бачить тільки NEXT_PUBLIC_*
    const raw = process.env.NEXT_PUBLIC_API_URL || '';
    return raw.replace(/\/$/, '');
  }, []);

  const togglePasswordVisibility = () => setShowPassword((v) => !v);

  const handleCheckboxChange = (event) => {
    setIsCheckboxChecked(event.target.checked);
  };

  const handleName = (event) => setName(event.target.value);
  const handleEmail = (event) => setEmail(event.target.value);
  const handlePassword = (event) => setPassword(event.target.value);

  const extractErrorMessage = (err) => {
    // універсально і без падінь
    const data = err?.response?.data;

    // FastAPI/Pydantic часто дає detail як масив
    if (Array.isArray(data?.detail) && data.detail.length) {
      const first = data.detail[0];

      // приклад: {msg:"Field required"} або {ctx:{reason:"..."}} або {detail:"..."}
      return (
        first?.ctx?.reason ||
        first?.msg ||
        first?.detail ||
        JSON.stringify(first)
      );
    }

    // Django / інші API
    if (typeof data === 'string') return data;
    if (data?.message) return data.message;
    if (data?.detail && typeof data.detail === 'string') return data.detail;

    return err?.message || 'Unknown error';
  };

  async function createUser() {
    setErrorMessage('');

    if (!apiUrl) {
      setErrorMessage(
        'API URL is empty. Check NEXT_PUBLIC_API_URL in the frontend environment.'
      );
      return;
    }

    if (!isCheckboxChecked) {
      setErrorMessage('You must agree to the privacy policy to create an account.');
      return;
    }

    // бек очікує: name, email, password, re_password
    const payload = {
      name: name.trim(),
      email: email.trim(),
      password: password,
      re_password: password,
    };

    try {
      const response = await axios.post(`${apiUrl}/api/auth/sign_up/`, payload, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });

      if (response.status === 200 || response.status === 201) {
        login(response.data, true);
        closeSignUpModal();
        return;
      }

      // якщо раптом статус не ок
      logout();
      setErrorMessage('Sign up failed.');
    } catch (err) {
      console.error('Error during sign up:', err);
      logout();
      setErrorMessage(extractErrorMessage(err));
    }
  }

  const closeModalAndCreateUser = () => {
    if (!isLoginDisabled) {
      createUser().catch((err) => console.error('Create user failed:', err));
    }
  };

  const handleOverlayClick = (event) => {
    if (event.target?.id === 'modal-overlay') {
      closeSignUpModal();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      closeSignUpModal();
    }
  };

  useEffect(() => {
    if (isSignUpModalOpen) {
      document.addEventListener('mousedown', handleOverlayClick);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleOverlayClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSignUpModalOpen]);

  return (
    <div>
      <Button onClick={openSignUpModal} title={sign_up} type="sign-up" />

      <Modal
        isOpen={isSignUpModalOpen}
        onClose={closeSignUpModal}
        className={styles.signup}
        type="create-user"
      >
        <div className={styles.singup__content}>
          <h1 className={styles.signup__title}>
            {create_account_modal.create_account}
          </h1>

          <div className={styles.signup__input_wrapper}>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder={create_account_modal.your_name}
              value={name}
              onChange={handleName}
              required
            />

            <Input
              id="email"
              name="email"
              type="email"
              placeholder={create_account_modal.e_mail}
              value={email}
              onChange={handleEmail}
              required
            />

            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder={create_account_modal.your_password}
              value={password}
              onChange={handlePassword}
              showPassword={showPassword}
              onShowPassword={togglePasswordVisibility}
              required
            />
          </div>

          <div className={styles.signup__checkbox__wrapper}>
            <div className={styles.signup__checkbox_block}>
              <Checkbox
                type="grey"
                checked={isCheckboxChecked}
                onChange={handleCheckboxChange}
              />
              <p className={styles.signup__checkbox__description}>
                {create_account_modal.consent_to}
                <Link className={styles.signup__checkbox__link} href="#">
                  {' '}
                  {create_account_modal.privacy_policy}
                </Link>
              </p>
            </div>
          </div>

          <div className={styles.signup__button_wrapper}>
            <Button
              onClick={closeModalAndCreateUser}
              title={sign_up}
              type="login-modal"
              disabled={isLoginDisabled}
            />
            <Button onClick={openLoginModal} title={log_in} type="signup-modal" />
          </div>

          {errorMessage && <p className={styles.error_message}>{errorMessage}</p>}

          <p className={styles.signup__notification}>
            {create_account_modal.no_unsolicited}
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default SignUp;
